import z from "zod";
import { approvedAtSchema, createdAtSchema, monthSchema, updatedAtSchema, uuidSchema, yearSchema } from "./common";
import { partnerResponseDtoSchema } from "./partner";
import { auditUserResponseDtoSchema } from "./user";
import { EPartnerLeaveFilterBy, EPartnerLeaveStatusv4, ERewardPenaltyStatusv4, EServiceCategory, EUnifiedPartnerLeaveReasonEnum } from "../constants";
import { paginationDtoSchema } from "./monthly-earnings";

/**
 * Schema for partner basic info in leave listing
 */
export const partnerBasicInfoSchema = z.object({
    id: uuidSchema().describe("Partner ID"),
    name: z.string().describe("Partner name"),
    phoneNumber: z.string().describe("Partner phone number"),
    services: z.array(z.nativeEnum(EServiceCategory)).describe("Service categories"),
});

export type IPartnerBasicInfoDto = z.infer<typeof partnerBasicInfoSchema>;

/**
 * Schema for penalty basic details in response
 */
export const penaltyBasicDetailsSchema = z.object({
    penaltyId: z.string().describe("Penalty ID"),
    ruleId: z.string().describe("Penalty rule ID"),
    ruleCode: z.string().describe("Penalty rule code"),
    amount: z.number().describe("Penalty amount"),
    status: z.string().describe("Penalty status (CALCULATED, APPROVED, REJECTED, CANCELLED)"),
    isReversed: z.boolean().describe("Whether this penalty has been reverted"),
});

export type IPenaltyBasicDetailsDto = z.infer<typeof penaltyBasicDetailsSchema>;

/**
 * Schema for estimated penalty per session
 * id and status are only present when penalty has been applied already
 */
export const estimatedPenaltyDtoSchema = z.object({
    amount: z.number().int().positive().describe("Estimated penalty amount"),
    basePayAmount: z
        .number()
        .int()
        .positive()
        .optional()
        .describe("Base pay amount used for calculation (if available)"),
    id: uuidSchema().nullish().describe("Penalty ID (only present when penalty has been applied)"),
    status: z
        .nativeEnum(ERewardPenaltyStatusv4)
        .nullish()
        .describe("Penalty status (only present when penalty has been applied)"),
});

export type IEstimatedPenaltyDto = z.infer<typeof estimatedPenaltyDtoSchema>;

/**
 * Schema for affected session details
 */
export const leaveAffectedSessionDtoSchema = z.object({
    sessionId: uuidSchema().describe("Session ID"),
    scheduledAt: z.number().int().positive().describe("Scheduled time in epoch milliseconds"),
    area: z.string().describe("Area"),
    city: z.string().describe("City"),
    name: z.string().describe("Name of the customer"),
    petNames: z.array(z.string()).describe("Names of pets"),
    basePayAmount: z.number().int().positive().describe("Base pay amount used for calculation"),
    penaltyAmount: z.number().int().nonpositive().describe("Total penalty amount for this session"),
    penalties: z.array(estimatedPenaltyDtoSchema).describe("Estimated penalties that would apply"),
});

export type ILeaveAffectedSessionDto = z.infer<typeof leaveAffectedSessionDtoSchema>;

/**
 * Schema for affected session with penalties
 */
export const affectedSessionSchema = z.object({
    sessionId: uuidSchema().describe("Session ID"),
    scheduledAt: z.number().int().positive().describe("Scheduled time in epoch milliseconds"),
    area: z.string().describe("Area"),
    city: z.string().describe("City"),
    petNames: z.array(z.string()).describe("Names of pets"),
    basePayAmount: z
        .number()
        .int()
        .nonnegative()
        .describe("Base pay amount the partner would have earned for this session"),
    penalties: z.array(penaltyBasicDetailsSchema).describe("Penalties levied for this session"),
});

export type IAffectedSessionDto = z.infer<typeof affectedSessionSchema>;

/**
 * Schema for leave penalty summary
 */
export const leavePenaltySummarySchema = z
    .object({
        sessions: z.array(affectedSessionSchema).describe("Affected sessions with their penalties"),
        basePayAmount: z.number().int().nonnegative().describe("Total base pay amount across all affected sessions"),
        penaltyAmount: z.number().int().nonnegative().describe("Total penalty amount across all affected sessions"),
        penaltyCount: z.number().int().nonnegative().describe("Number of penalties affected by this leave"),
        penaltyStatus: z.nativeEnum(ERewardPenaltyStatusv4).nullish().describe("Penalty status"),
        sessionCount: z.number().int().nonnegative().describe("Number of affected sessions"),
    })
    .nullish();

export type ILeavePenaltySummaryDto = z.infer<typeof leavePenaltySummarySchema>;

/**
 * Schema for partner leave list item response
 */
export const partnerLeaveAdminResponseDtoSchema = z.object({
    id: uuidSchema().describe("Leave ID"),
    partner: partnerResponseDtoSchema.describe("Partner basic information"),
    startsAt: z.number().int().positive().describe("Start time of the leave in epoch milliseconds"),
    endsAt: z.number().int().positive().describe("End time of the leave in epoch milliseconds"),
    reason: z.nativeEnum(EUnifiedPartnerLeaveReasonEnum).nullish().describe("Reason for the leave"),
    status: z.nativeEnum(EPartnerLeaveStatusv4).describe("Status of the leave (APPLIED, APPROVED, REJECTED)"),
    proofUrls: z.array(z.string().url()).nullish().describe("Array of proof image URLs"),
    sessions: z.array(leaveAffectedSessionDtoSchema).describe("Sessions that would be affected by the leave"),
    penaltyAmount: z.number().int().nonpositive().describe("Total estimated penalty amount"),
    penaltyStatus: z.nativeEnum(ERewardPenaltyStatusv4).nullish().describe("Overall penalty status"),
    basePayAmount: z.number().int().nonnegative().describe("Base pay amount used for calculation"),
    sessionCount: z.number().int().nonnegative().describe("Number of sessions that would be affected"),
    createdAt: createdAtSchema.describe("Time when the leave was created in epoch milliseconds"),
    updatedAt: updatedAtSchema.describe("Time when the leave was last updated in epoch milliseconds"),
    createdBy: auditUserResponseDtoSchema.nullish().describe("User who created the leave"),
    updatedBy: auditUserResponseDtoSchema.nullish().describe("User who last updated the leave"),
});

export type IPartnerLeaveAdminResponseDto = z.infer<typeof partnerLeaveAdminResponseDtoSchema>;

/**
 * Schema for searching partner leaves (admin view)
 * Extends paginationSchema to include page and limit
 */
export const searchPartnerLeavesRequestSchema = paginationDtoSchema.extend({
    /**
     * Optional filter by leave status
     */
    statuses: z.array(z.nativeEnum(EPartnerLeaveStatusv4)).nullish(),

    /**
     * Optional filter by service category
     */
    serviceCategories: z.array(z.nativeEnum(EServiceCategory)).nullish(),

    /**
     * Optional filter by partner IDs
     */
    partnerIds: z.array(z.string().uuid()).nullish(),

    /**
     * Optional filter by filterBy enum (ACTIVE, UPCOMING, COMPLETED, ALL)
     */
    filterBy: z.nativeEnum(EPartnerLeaveFilterBy).nullish(),

    /**
     * Optional date range filter - leaves starting from this date
     */
    startsAtFrom: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),

    /**
     * Optional date range filter - leaves starting until this date
     */
    startsAtTo: z.preprocess(val => (val ? Number(val) : undefined), z.number().int().positive().nullish()),

    /**
     * Optional search term (searches partner name/phone)
     */
    search: z.string().optional(),

    /**
     * Optional filter by month (1-12)
     */
    month: monthSchema().nullish(),

    /**
     * Optional filter by year (e.g., 2024)
     */
    year: yearSchema().nullish(),

    /**
     * Optional filter by city code
     */
    cityCode: z.string().trim().min(1).nullish(),

    /**
     * Optional filter by sub city code
     */
    subCityCode: z.string().trim().min(1).nullish(),

    /**
     * Optional filter by hub code
     */
    hubCode: z.string().trim().min(1).nullish(),
});

/**
 * Type for query parameters when searching partner leaves
 */
export type SearchPartnerLeavesRequestDto = z.infer<typeof searchPartnerLeavesRequestSchema>;

/**
 * DTO for creating a partner leave request (admin view)
 * Includes partnerId in the body since admin can create leaves for any partner
 */
export const createPartnerLeaveRequestDtoSchema = z.object({
    partnerId: uuidSchema().describe("Partner ID for whom the leave is being created"),
    startsAt: z.number().int().positive().describe("Start time of the leave in epoch milliseconds"),
    endsAt: z.number().int().positive().describe("End time of the leave in epoch milliseconds"),
    reason: z.string().min(1).describe("Reason for the leave"),
    proofUrls: z.array(z.string().url()).nullish().describe("Array of proof image URLs"),
});

export type ICreatePartnerLeaveRequestDto = z.infer<typeof createPartnerLeaveRequestDtoSchema>;

/**
 * DTO for cancelling a partner leave request (admin view)
 */
export const cancelPartnerLeaveRequestDtoSchema = z.object({
    cancellationReason: z.string().min(1).describe("Reason for cancelling the leave"),
});

export type ICancelPartnerLeaveRequestDto = z.infer<typeof cancelPartnerLeaveRequestDtoSchema>;

/**
 * DTO for partner leave response
 */
export const partnerLeaveResponseDtoSchema = z.object({
    id: uuidSchema().describe("Unique identifier for the leave"),
    partner: partnerResponseDtoSchema.describe("Partner details"),
    startsAt: z.number().int().positive().describe("Start time of the leave in epoch milliseconds"),
    endsAt: z.number().int().positive().describe("End time of the leave in epoch milliseconds"),
    reason: z.nativeEnum(EUnifiedPartnerLeaveReasonEnum).nullish().describe("Reason for the leave"),
    status: z.nativeEnum(EPartnerLeaveStatusv4).describe("Status of the leave (APPLIED, APPROVED, REJECTED)"),
    approvedBy: auditUserResponseDtoSchema.nullish().describe("User who approved the leave"),
    approvedAt: approvedAtSchema.nullish().describe("Time when the leave was approved in epoch milliseconds"),
    proofUrls: z.array(z.string().url()).nullish().describe("Array of proof image URLs"),
    approvalComments: z.string().nullish().nullish().describe("Comments provided during approval"),
    rejectedBy: auditUserResponseDtoSchema.nullish().describe("User who rejected the leave"),
    rejectedAt: approvedAtSchema.nullish().describe("Time when the leave was rejected in epoch milliseconds"),
    rejectionComments: z.string().nullish().nullish().describe("Comments provided during rejection"),

    sessions: z.array(leaveAffectedSessionDtoSchema).describe("Sessions that would be affected by the leave"),
    penaltyAmount: z.number().int().nonpositive().describe("Total estimated penalty amount"),
    penaltyStatus: z.nativeEnum(ERewardPenaltyStatusv4).nullish().describe("Overall penalty status"),
    basePayAmount: z.number().int().nonnegative().describe("Base pay amount used for calculation"),
    sessionCount: z.number().int().nonnegative().describe("Number of sessions that would be affected"),

    // penaltySummary: leavePenaltySummarySchema.describe("Summary of affected sessions and penalties"),
    createdAt: createdAtSchema.describe("Time when the leave was created in epoch milliseconds"),
    updatedAt: updatedAtSchema.describe("Time when the leave was last updated in epoch milliseconds"),
    createdBy: auditUserResponseDtoSchema.nullish().describe("User who created the leave"),
    updatedBy: auditUserResponseDtoSchema.nullish().describe("User who last updated the leave"),
});

export type IPartnerLeaveResponseDtoV4 = z.infer<typeof partnerLeaveResponseDtoSchema>;
